import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Grid, TextField, InputAdornment, Typography } from "@mui/material";
import { Tooltip as MuiTooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const RetentionCalculator = ({ isPublicMode = false, isUnlocked = true, onGenerateReport, userEmail }) => {
    const location = useLocation();
    const isPublic = isPublicMode || location.pathname === "/retention-calculator" || location.pathname === "/free-retention-calculator";
    const showCTA = location.pathname !== "/free-retention-calculator";
    const showGenerateReportButton = location.pathname === "/free-retention-calculator";
    const isFreeRetentionPage = location.pathname === "/free-retention-calculator";
    const shouldShowBlockingOverlay = isFreeRetentionPage && !isUnlocked;
    
    // InfoTooltip component
    const InfoTooltip = ({ title, placement = 'top' }) => (
        <MuiTooltip
            title={<span style={{ fontSize: 13, color: '#fff' }}>{title}</span>}
            placement={placement}
            arrow
            enterTouchDelay={0}
            leaveTouchDelay={3000}
            disableHoverListener={false}
            disableFocusListener={false}
            disableTouchListener={false}
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: '#222',
                        color: '#fff',
                        fontSize: { xs: 12, sm: 13 },
                        borderRadius: 1,
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.5, sm: 0.7 },
                        boxShadow: 'none',
                        maxWidth: { xs: 250, sm: 300 },
                        zIndex: 9999,
                    }
                },
                arrow: {
                    sx: {
                        color: '#222',
                    }
                }
            }}
        >
            <InfoOutlinedIcon 
                sx={{ 
                    color: '#888', 
                    fontSize: { xs: 18, sm: 16 }, 
                    verticalAlign: 'middle', 
                    opacity: 1, 
                    ml: 0.5,
                    cursor: 'pointer'
                }} 
            />
        </MuiTooltip>
    );
    
    // State to control form/chart visibility for free retention calculator
    const [reportGenerated, setReportGenerated] = useState(false);
    
    // Initial values based on page type
    const getInitialValues = () => {
        if (isFreeRetentionPage) {
            return {
                activeClients: 0,
                averageMonthlyRevenue: 0,
                currentRetentionRate: 0,
                improvedRetentionRate: 0,
                acquisitionCost: 0,
                monthlyClientLifetime: 0
            };
        }
        return {
            activeClients: 50,
            averageMonthlyRevenue: 5000,
            currentRetentionRate: 80,
            improvedRetentionRate: 85,
            acquisitionCost: 5000,
            monthlyClientLifetime: 12
        };
    };

    // State for input values
    const [inputs, setInputs] = useState(getInitialValues());

    // State for calculated results
    const [results, setResults] = useState({
        currentAnnualRevenue: 0,
        improvedAnnualRevenue: 0,
        revenueIncrease: 0,
        savedAcquisitionCosts: 0,
        additionalLifetimeValue: 0,
        totalImpact: 0
    });

    // Chart data
    const [chartData, setChartData] = useState([]);

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInputs({
            ...inputs,
            [name]: parseFloat(value)
        });
    };

    // Calculate results whenever inputs change
    useEffect(() => {
        // Current annual metrics
        const currentAnnualChurn = 100 - inputs.currentRetentionRate;
        const currentLostClientsPerYear = (inputs.activeClients * currentAnnualChurn) / 100;
        const currentAnnualRevenue = inputs.activeClients * inputs.averageMonthlyRevenue * 12;

        // Improved annual metrics
        const improvedAnnualChurn = 100 - inputs.improvedRetentionRate;
        const improvedLostClientsPerYear = (inputs.activeClients * improvedAnnualChurn) / 100;
        const improvedAnnualRevenue = currentAnnualRevenue +
            ((currentLostClientsPerYear - improvedLostClientsPerYear) * inputs.averageMonthlyRevenue * 12);

        // Calculate saved acquisition costs
        const clientsSavedPerYear = currentLostClientsPerYear - improvedLostClientsPerYear;
        const savedAcquisitionCosts = clientsSavedPerYear * inputs.acquisitionCost;

        // Calculate additional lifetime value from longer relationships
        const avgMonthsGained = inputs.improvedRetentionRate > inputs.currentRetentionRate
            ? (inputs.monthlyClientLifetime * inputs.improvedRetentionRate / inputs.currentRetentionRate) - inputs.monthlyClientLifetime
            : 0;
        const additionalLifetimeValue = inputs.activeClients * inputs.averageMonthlyRevenue * avgMonthsGained;

        // Total financial impact
        const revenueIncrease = improvedAnnualRevenue - currentAnnualRevenue;
        const totalImpact = revenueIncrease + savedAcquisitionCosts + additionalLifetimeValue;

        // Update results
        setResults({
            currentAnnualRevenue,
            improvedAnnualRevenue,
            revenueIncrease,
            savedAcquisitionCosts,
            additionalLifetimeValue,
            totalImpact
        });

        // Update chart data - showing exponential impact of retention improvements
        const baseRetention = inputs.currentRetentionRate;
        const chartData = [];

        // Calculate impact for different retention rate improvements
        for (let i = 0; i <= 15; i += 5) {
            if (i === 0) {
                chartData.push({
                    retention: `${baseRetention}%`,
                    impact: 0,
                    label: 'Current'
                });
            } else {
                const newRetention = Math.min(baseRetention + i, 100);
                const improvementFactor = i === 5 ? 1 : i === 10 ? 1.4 : i === 15 ? 1.9 : 1;
                const baseImpact = totalImpact > 0 ? totalImpact : 100000;
                const impact = i === 5 ? baseImpact : baseImpact * improvementFactor;

                chartData.push({
                    retention: `${newRetention}%`,
                    impact: impact,
                    label: i === 5 ? '+5%' : i === 10 ? '+10%' : '+15%'
                });
            }
        }

        setChartData(chartData);
    }, [inputs]);

    // Handle generate report button click
    const handleGenerateReport = async () => {
        setReportGenerated(true);

        // Send to Facebook Pixel when calculator is completed
        if (typeof window !== 'undefined' && window.fbq && isFreeRetentionPage) {
            window.fbq('trackCustom', 'CalculatorCompleted', {
                calculatorType: 'RetentionCalculator'
            });
        }
        
        // Send financial impact data to webhook
        if (isFreeRetentionPage) {
            try {
                const webhookData = {
                    email: userEmail,
                    activeClients: inputs.activeClients,
                    averageMonthlyRevenue: inputs.averageMonthlyRevenue,
                    currentRetentionRate: inputs.currentRetentionRate,
                    improvedRetentionRate: inputs.improvedRetentionRate,
                    acquisitionCost: inputs.acquisitionCost,
                    monthlyClientLifetime: inputs.monthlyClientLifetime,
                    protectedRevenue: formatCurrency(results.revenueIncrease),
                    savedAcquisitionCosts: formatCurrency(results.savedAcquisitionCosts),
                    additionalLifetimeValue: formatCurrency(results.additionalLifetimeValue),
                    totalFinancialImpact: formatCurrency(results.totalImpact)
                };

                await axios.post(
                    'https://services.leadconnectorhq.com/hooks/hz6swxxqV8ZMTuyTG0hP/webhook-trigger/69af08ed-31fd-4fe0-ae47-f3e1e6b4b229',
                    webhookData,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    }
                );
            } catch (error) {
                console.error('Error sending financial impact data:', error);
                // Don't show error to user, just log it
            }
        }
        
        if (onGenerateReport) {
            onGenerateReport();
        }
    };

    // Format currency
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(value);
    };

    return (
        <div className="relative bg-white rounded-lg shadow-lg p-6 max-w-5xl mx-auto">
            {/* Internal blocking overlay for free-retention-calculator page */}
            {shouldShowBlockingOverlay && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 10,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <div className="text-center p-6">
                        <div className="mb-4 flex justify-center">
                            <svg 
                                width="80" 
                                height="80" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="#7143d8" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                            >
                                <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
                                <circle cx="12" cy="16" r="1"></circle>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Calculator Locked</h3>
                        <p className="text-gray-600">Complete the form to unlock this calculator</p>
                    </div>
                </div>
            )}
            
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Retention ROI Calculator</h1>
                <p className="text-gray-600 text-lg">Calculate the financial impact of improving your client retention</p>
            </div>

            <div className={isFreeRetentionPage ? "flex justify-center" : "grid md:grid-cols-2 gap-8"}>
                {/* Input Section - Hide after report generation in free retention page */}
                {(!isFreeRetentionPage || !reportGenerated) && (
                    <div className={`bg-gray-50 p-6 rounded-lg ${isFreeRetentionPage ? 'max-w-md w-full' : ''}`}>
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">Business Information</h2>

                        <Grid container rowSpacing={3} columnSpacing={4}>
                            {/* Number of active clients */}
                            <Grid item xs={12}>
                                <TextField
                                    sx={{
                                        backgroundColor: "white"
                                    }}
                                    label="Number of Active Clients"
                                    type="number"
                                    name="activeClients"
                                    value={inputs.activeClients}
                                    onChange={handleInputChange}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>

                            {/* Avg monthly revenue */}
                            <Grid item xs={12}>
                                <TextField
                                    sx={{
                                        backgroundColor: "white"
                                    }}
                                    label="Average Monthly Revenue per Client"
                                    type="number"
                                    name="averageMonthlyRevenue"
                                    value={inputs.averageMonthlyRevenue}
                                    onChange={handleInputChange}
                                    fullWidth
                                    size="small"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>

                            {/* Current retention */}
                            <Grid item xs={12}>
                                <TextField
                                    sx={{
                                        backgroundColor: "white"
                                    }}
                                    label="Current Annual Retention Rate (%)"
                                    type="number"
                                    name="currentRetentionRate"
                                    value={inputs.currentRetentionRate}
                                    onChange={handleInputChange}
                                    inputProps={{ min: 1, max: 99 }}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>

                            {/* Target retention */}
                            <Grid item xs={12}>
                                <TextField
                                    sx={{
                                        backgroundColor: "white"
                                    }}
                                    label="Target Annual Retention Rate (%)"
                                    type="number"
                                    name="improvedRetentionRate"
                                    value={inputs.improvedRetentionRate}
                                    onChange={handleInputChange}
                                    inputProps={{ min: 1, max: 100 }}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>

                            {/* Acquisition cost */}
                            <Grid item xs={12}>
                                <TextField
                                    sx={{
                                        backgroundColor: "white"
                                    }}
                                    label="Average Client Acquisition Cost"
                                    type="number"
                                    name="acquisitionCost"
                                    value={inputs.acquisitionCost}
                                    onChange={handleInputChange}
                                    fullWidth
                                    size="small"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            </Grid>

                            {/* Client lifetime */}
                            <Grid item xs={12}>
                                <TextField
                                    sx={{
                                        backgroundColor: "white"
                                    }}
                                    label="Average Client Lifetime (months)"
                                    type="number"
                                    name="monthlyClientLifetime"
                                    value={inputs.monthlyClientLifetime}
                                    onChange={handleInputChange}
                                    inputProps={{ min: 1 }}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                        
                        {/* Generate Report Button*/}
                        {showGenerateReportButton && isUnlocked && (
                            <div className="mt-6 flex justify-center">
                                <button 
                                    className="bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold py-4 px-10 rounded-lg shadow-md hover:from-purple-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
                                    style={{
                                        fontSize: '18px',
                                        minWidth: '250px'
                                    }}
                                    onClick={handleGenerateReport}
                                >
                                    Generate Your Custom Report
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Results Section - Show always in normal pages, only after report generation in free retention page */}
                {(!isFreeRetentionPage || reportGenerated) && (
                    <div className={isFreeRetentionPage && reportGenerated ? "max-w-4xl w-full" : ""}>
                        <div className="bg-gray-50 p-6 rounded-lg mb-6">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">Financial Impact</h2>

                            <div className="space-y-3">
                                <div className="grid grid-cols-2">
                                    <span className="text-gray-600 flex items-center">
                                        Protected Revenue:
                                        {isFreeRetentionPage && (
                                            <InfoTooltip title="The amount of revenue you'll keep by preventing client churn at your improved retention rate. This represents money that would have walked out the door with lost clients." />
                                        )}
                                    </span>
                                    <span
                                        className="font-semibold text-right">{formatCurrency(results.revenueIncrease)}</span>
                                </div>

                                <div className="grid grid-cols-2">
                                    <span className="text-gray-600 flex items-center">
                                        Saved Acquisition Costs:
                                        {isFreeRetentionPage && (
                                            <InfoTooltip title="The marketing and sales dollars you won't need to spend replacing churned clients. Every client you keep saves you the full cost of finding and onboarding a new one." />
                                        )}
                                    </span>
                                    <span
                                        className="font-semibold text-right">{formatCurrency(results.savedAcquisitionCosts)}</span>
                                </div>

                                <div className="grid grid-cols-2">
                                    <span className="text-gray-600 flex items-center">
                                        Additional Lifetime Value:
                                        {isFreeRetentionPage && (
                                            <InfoTooltip title="The extra revenue generated when clients stay longer at your improved retention rate. Longer relationships mean more revenue per client over time." />
                                        )}
                                    </span>
                                    <span
                                        className="font-semibold text-right">{formatCurrency(results.additionalLifetimeValue)}</span>
                                </div>

                                <div className="border-t pt-2 grid grid-cols-2">
                                    <span className="text-gray-800 font-bold flex items-center">
                                        Total Financial Impact:
                                        {isFreeRetentionPage && (
                                            <InfoTooltip title="Your complete retention improvement value combining all three benefits above. This is the real dollar impact of achieving your target retention rate." />
                                        )}
                                    </span>
                                    <span
                                        className="font-bold text-right text-green-600">{formatCurrency(results.totalImpact)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-lg">
                            <h2 className="text-xl font-semibold text-gray-800 mb-4">Exponential Impact of Retention
                                Improvements</h2>
                            <div className="h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={chartData}
                                        margin={{ top: 20, right: 30, left: 80, bottom: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="retention" />
                                        <YAxis tickFormatter={(value) => formatCurrency(value)} />
                                        <Tooltip
                                            formatter={(value) => [formatCurrency(value), "Financial Impact"]}
                                            labelFormatter={(value) => `Retention Rate: ${value}`}
                                        />
                                        <Bar
                                            dataKey="impact"
                                            fill="#03BC72"
                                            name="Financial Impact"
                                            label={{ position: 'top', formatter: (value) => chartData.find(item => item.impact === value)?.label || '' }}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <p className="text-gray-600 text-sm mt-3">
                                The financial impact grows exponentially as retention rates improve. Each percentage point improvement in retention has a greater impact on your bottom line.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Email confirmation message - Only show on free retention page after report generated */}
            {isFreeRetentionPage && reportGenerated && (
                <>
                    <div className="mt-6 bg-yellow-300 p-3 sm:p-4 rounded-lg text-center max-w-2xl mx-auto">
                        <p className="text-xl text-gray-800">
                            Your full retention report has been emailed & will arrive in a few minutes!
                        </p>
                    </div>
                    <div className="mt-3 text-center">
                        <p className="text-base font-bold text-gray-800">
                            Read below for next steps...
                        </p>
                    </div>
                </>
            )}

            {/* Business explanation section - Hide until report generation in free retention page */}
            {(!isFreeRetentionPage || reportGenerated) && (
                <div className="mt-8 bg-gray-50 p-6 rounded-lg">
                    <h2 className="text-xl font-semibold text-gray-800 mb-3">What This Means For Your Business</h2>
                    <p className="text-gray-600 mb-3">
                        Based on your inputs, improving your client retention rate from {inputs.currentRetentionRate}% to {inputs.improvedRetentionRate}% would result in a total financial impact of {formatCurrency(results.totalImpact)} over the next year.
                    </p>
                    <p className="text-gray-600 mb-3">
                        This includes {formatCurrency(results.revenueIncrease)} in protected revenue, {formatCurrency(results.savedAcquisitionCosts)} in saved acquisition costs, and {formatCurrency(results.additionalLifetimeValue)} in additional lifetime value from longer client relationships.
                    </p>
                    <p className="text-gray-700 font-medium">
                        ClientBloom helps businesses achieve retention improvements of 5-15% within the first 90 days through our AI-powered Client Retention Score™ and proactive intervention system.
                    </p>
                </div>
            )}
            {!isFreeRetentionPage && isPublic && showCTA && (
            <div className="mt-8 text-center">
                <p className="text-gray-600 mb-3">Want to see how ClientBloom can help you achieve these results?</p>
                <a href="https://go.clientbloom.ai/schedule-call?utm_source=app" rel="noopener noreferrer">
                <button className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-8 rounded-lg font-semibold shadow-md hover:from-purple-700 hover:to-indigo-700 transition-all">
                    Book a Personalized Demo
                </button>
                </a>
            </div>
            )}
            {isPublic && (
            <div className="logo-rc absolute top-0 left-0 p-4">
            <img src="/footer_logo.svg" alt="ClientBloom Logo" className="h-6" />
            </div>
            )}

        </div>
    );
};

export default RetentionCalculator;